export interface ClocktowerPlazaChallengeConfig {
  readonly id: string;
  readonly skillId: string;
  readonly kind: 'set-time' | 'elapsed-time';
  readonly prompt: string;
  readonly targetHour: number;
  readonly targetMinute: number;
  readonly startingHour: number;
  readonly startingMinute: number;
  readonly elapsedFrom?: { readonly hour: number; readonly minute: number; readonly minutesLater: number };
  readonly hints: readonly string[];
}

export function formatClockTime(hour: number, minute: number) {
  return `${hour}:${String(minute).padStart(2, '0')}`;
}

export function validateClocktowerPlazaChallenge<T extends ClocktowerPlazaChallengeConfig>(config: T) {
  const hours = [config.targetHour, config.startingHour, config.elapsedFrom?.hour]
    .filter((value): value is number => value !== undefined);
  const minutes = [config.targetMinute, config.startingMinute, config.elapsedFrom?.minute]
    .filter((value): value is number => value !== undefined);
  if (!config.id.trim() || !config.skillId.trim() || !config.prompt.trim() || config.hints.length < 3) {
    throw new RangeError('Clocktower Plaza requires identifiers, a prompt, and three progressive hints.');
  }
  if (hours.some(hour => !Number.isInteger(hour) || hour < 1 || hour > 12)
    || minutes.some(minute => !Number.isInteger(minute) || minute < 0 || minute > 55 || minute % 5 !== 0)) {
    throw new RangeError('Clocktower Plaza times must use hours 1–12 and five-minute intervals.');
  }
  if (config.kind === 'elapsed-time') {
    const elapsed = config.elapsedFrom;
    if (!elapsed || elapsed.minutesLater <= 0 || elapsed.minutesLater % 5 !== 0) {
      throw new RangeError('Elapsed-time challenges require a positive five-minute interval.');
    }
    const startMinutes = (elapsed.hour % 12) * 60 + elapsed.minute;
    const expected = (startMinutes + elapsed.minutesLater) % (12 * 60);
    const expectedHour = Math.floor(expected / 60) || 12;
    if (expectedHour !== config.targetHour || expected % 60 !== config.targetMinute) {
      throw new RangeError('Elapsed-time target must match the authored start and duration.');
    }
  }
  return config;
}

export const CLOCKTOWER_PLAZA_CHALLENGES = [
  validateClocktowerPlazaChallenge({
    id: 'clocktower-five-minute-1',
    skillId: 'math-tell-time-five-minutes',
    kind: 'set-time',
    prompt: 'The morning market opens at 3:20. Set the royal clock.',
    targetHour: 3,
    targetMinute: 20,
    startingHour: 1,
    startingMinute: 0,
    hints: [
      'Move the short hand to the hour 3 first.',
      'Each minute-hand tap moves 5 minutes. Count 5, 10, 15, 20.',
      'The long hand points to 4 for 20 minutes past the hour.',
    ],
  }),
  validateClocktowerPlazaChallenge({
    id: 'clocktower-five-minute-2',
    skillId: 'math-tell-time-five-minutes',
    kind: 'set-time',
    prompt: 'The lantern parade begins at 7:45. Synchronize the plaza clock.',
    targetHour: 7,
    targetMinute: 45,
    startingHour: 4,
    startingMinute: 10,
    hints: [
      'Set the short hand to 7.',
      'Count the minute marks by fives until you reach 45.',
      'Forty-five minutes puts the long hand on 9.',
    ],
  }),
  validateClocktowerPlazaChallenge({
    id: 'clocktower-five-minute-3',
    skillId: 'math-tell-time-five-minutes',
    kind: 'set-time',
    prompt: 'The final tower bell rings at 11:55. Set the hands precisely.',
    targetHour: 11,
    targetMinute: 55,
    startingHour: 8,
    startingMinute: 25,
    hints: [
      'The short hand belongs at 11.',
      'Fifty-five minutes is one five-minute step before a new hour.',
      'The long hand points to 11 when the minutes are 55.',
    ],
  }),
  validateClocktowerPlazaChallenge({
    id: 'clocktower-elapsed-foundation',
    skillId: 'math-elapsed-time-foundations',
    kind: 'elapsed-time',
    prompt: 'A gear repair starts at 9:15 and takes 30 minutes. Set the finish time.',
    targetHour: 9,
    targetMinute: 45,
    startingHour: 9,
    startingMinute: 15,
    elapsedFrom: { hour: 9, minute: 15, minutesLater: 30 },
    hints: [
      'Keep the hour at 9 and count forward 30 minutes.',
      'Thirty minutes is six jumps of 5 minutes.',
      'Count from 9:15: 20, 25, 30, 35, 40, 45. The finish time is 9:45.',
    ],
  }),
] as const satisfies readonly ClocktowerPlazaChallengeConfig[];

export const CLOCKTOWER_PLAZA_COMPLETION_EVENT = 'clocktower-plaza-synchronized';
